import { FC, useCallback, useMemo } from 'react';
import keyBy from 'lodash/keyBy';
import { Member, useMembersQuery } from 'api/MembersAPI';
import { MembersContext } from './MembersContext';
import { FormattedMembers } from '../types';

export const MembersContextProvider: FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const {
    data: membersRaw = {},
    isLoading: isLoadingMembers,
    refetch: refetchMembers,
  } = useMembersQuery();

  const members: Member[] = membersRaw?.members || [];

  const membersByUserId = useMemo(
    () => (!isLoadingMembers ? keyBy(members as Member[], (member) => member.userId) : {}),
    [members],
  );

  const defaultState = { orgAdmins: [], projectMemberships: {}, teamMemberships: {} };
  const formattedMembers = useMemo(
    () =>
      !isLoadingMembers
        ? members?.reduce((acc: FormattedMembers, element: Member) => {
            const { orgAdmins, projectMemberships, teamMemberships } = acc;
            // collect admins for use later on every role
            if (element.orgAdmin) {
              orgAdmins.push(element.userId);
            }

            element.projectMemberships.forEach(({ roleId }) => {
              projectMemberships[roleId] ??= [];
              projectMemberships[roleId].push(element.userId);
            });

            element.teamMemberships.forEach(({ roleId }) => {
              teamMemberships[roleId] ??= [];
              teamMemberships[roleId].push(element.userId);
            });
            return acc;
          }, defaultState)
        : defaultState,
    [members],
  );

  const getMemberByUserId = useCallback((userId: string) => membersByUserId[userId], [members]);

  const {
    orgAdmins = [],
    projectMemberships = {},
    teamMemberships = {},
  } = formattedMembers as FormattedMembers;

  return (
    <MembersContext.Provider
      value={{
        getMemberByUserId,
        isLoadingMembers,
        members,
        membersByUserId,
        orgAdmins,
        projectMemberships,
        refetchMembers,
        teamMemberships,
      }}
    >
      {children}
    </MembersContext.Provider>
  );
};
